import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import { formatiereEuro } from '../../warenkorb';
import {
  fokusFalleTaste,
  tastenName,
  useFokusRueckgabe,
  useFokusWaechter,
  useTastensperre,
} from './fokus';
import { sperrMeldung, type Tastenregel } from './logik';
import './zahlung-v2.css';

/**
 * Gemeinsame Hülle der Zahlungsdialoge V2 (Figma ZD-FINAL, Section 275:2598): Betragsband in
 * Markenfarbe mit Tastenhinweis, Inhalt, Fußleiste. Enthält Fokusfalle, Fokusrückgabe und die
 * Tastensperre (sichtbarer Hinweis, wenn eine gesperrte Taste gedrückt wird).
 */
export function ZahlungsHuelle(props: {
  /** Art rechts im Band, zugleich Dialogtitel („Barzahlung“, „Kartenzahlung“ …). */
  art: string;
  betragLabel: string;
  betragCent: number;
  info?: string | undefined;
  regel: Tastenregel;
  beschaeftigt: boolean;
  klasse: string;
  /** Element, das beim Öffnen bzw. bei Wechsel von `fokusSchluessel` den Fokus bekommt. */
  startFokus?: RefObject<HTMLElement | null>;
  fokusSchluessel?: string;
  onTaste?: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
  children: ReactNode;
  fuss: ReactNode;
}) {
  const huelle = useRef<HTMLDivElement | null>(null);
  const titelId = useId();
  const hinweisId = useId();
  const [sperrHinweis, setSperrHinweis] = useState<string | null>(null);
  const { startFokus, fokusSchluessel, regel } = props;

  useFokusRueckgabe();
  useEffect(() => {
    const ziel = startFokus?.current ?? huelle.current;
    ziel?.focus();
  }, [startFokus, fokusSchluessel]);

  useFokusWaechter(huelle, () => startFokus?.current ?? null);
  useTastensperre(regel.gesperrt, (taste) => {
    setSperrHinweis(`${tastenName(taste)}: ${sperrMeldung(regel, taste)}`);
  });
  useEffect(() => {
    if (sperrHinweis === null) {
      return;
    }
    const t = setTimeout(() => {
      setSperrHinweis(null);
    }, 4_000);
    return () => {
      clearTimeout(t);
    };
  }, [sperrHinweis]);

  return (
    <div className="zahlung-dialog zv2">
      <div
        ref={huelle}
        className={`zv2-huelle ${props.klasse}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        aria-describedby={hinweisId}
        aria-busy={props.beschaeftigt}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (huelle.current !== null) {
            fokusFalleTaste(e, huelle.current);
          }
          props.onTaste?.(e);
        }}
      >
        <header className="zv2-band">
          <div className="zv2-band-betrag">
            <span className="zv2-band-label">{props.betragLabel}</span>
            <strong className="zv2-betrag">{formatiereEuro(props.betragCent)} €</strong>
          </div>
          <div className="zv2-band-info">
            <h2 id={titelId}>{props.art}</h2>
            {props.info !== undefined && <span className="zv2-band-zeile">{props.info}</span>}
            <span
              className={sperrHinweis === null ? 'zv2-tastenhinweis' : 'zv2-tastenhinweis gesperrt'}
              id={hinweisId}
            >
              <span aria-hidden="true">⌨ </span>
              {regel.hinweis}
            </span>
          </div>
        </header>
        <p className="zv2-sperrhinweis" role="status" aria-live="polite">
          {sperrHinweis}
        </p>
        <div className="zv2-inhalt">{props.children}</div>
        <footer className="zv2-fuss">{props.fuss}</footer>
      </div>
    </div>
  );
}

/** Tastenkürzel-Anzeige in einer Taste (für Hilfstechnik verborgen; dort `aria-keyshortcuts`). */
export function Kuerzel(props: { children: string }) {
  return (
    <kbd className="zv2-kuerzel" aria-hidden="true">
      {props.children}
    </kbd>
  );
}
